import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { formatStat, snakeCaseToTitleCase } from '@shared-lib/utils/format.utils';
import { of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { CustomPayloadService } from '../../../projects/my-app/src/app/shared/dashboard-payload-services/custom-payload.service';

@Component({
  selector: 'app-custom-number-stats',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './number-stats.component.html',
  styleUrl: './number-stats.component.scss',
})
export class NumberStatsComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  stat!: string;
  deltaText = '';
  deltaDirection: 'positive' | 'negative' | 'neutral' = 'neutral';

  private filterSub!: Subscription;
  private unSubscribe$ = new Subject<void>();
  public hasError = false;
  public errorMessage = '';

  constructor(
    private http: HttpClient,
    private configService: CustomPayloadService,
    private standardConfigService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
  ) {}

  ngAfterViewInit(): void {
    this.filterSub = this.loadStat(this.getInitialFiltersForWidget())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => {
          this.hasError = true;
          this.errorMessage = 'Error loading stat.';
          console.error('Error loading stat:', err);
        },
      });

    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          if (this.data.callApiInDefault === false) {
            let mandatoryFiltersSelected = true;
            const missingMandatoryFilterNames: string[] = [];

            if (this.data.mandatoryFilters && this.data.mandatoryFilters.length > 0) {
              for (const mandatoryFilterKey of this.data.mandatoryFilters) {
                const filterValue = filters.queryParams[mandatoryFilterKey] || filters.requestParams[mandatoryFilterKey];
                if (!filterValue || filterValue.length === 0) {
                  mandatoryFiltersSelected = false;
                  missingMandatoryFilterNames.push(snakeCaseToTitleCase(mandatoryFilterKey));
                }
              }
            }

            if (!mandatoryFiltersSelected) {
              this.hasError = true;
              this.errorMessage = `Please select ${missingMandatoryFilterNames.join(' and ')} to see the data.`;
              return of(null);
            }
          }

          this.hasError = false;
          return this.loadStat(filters);
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (res === null) return;

          try {
            this.updateStat(res);
            this.hasError = !this.stat && this.data?.displayValue === undefined && this.data?.staticValue === undefined;
            this.errorMessage = this.hasError ? 'No data found for the selected filters.' : '';
          } catch {
            this.hasError = true;
            this.errorMessage = 'Error processing data.';
          }
        },
        error: (err: any) => {
          this.hasError = true;
          this.errorMessage = 'Error loading stat.';
          console.error('Error loading stat:', err);
        },
      });
  }

  get formattedTitle(): string {
    return this.data?.title?.replace(/\n/g, '<br>') || '';
  }

  get note(): string {
    return this.data?.note || '';
  }

  get subtitle(): string {
    return this.data?.subtitle || '';
  }

  get badges(): Array<{ label: string; value: string | number }> {
    return Array.isArray(this.data?.badges) ? this.data.badges : [];
  }

  get cardClass(): string {
    return this.data?.cardClass || '';
  }

  get iconName(): string {
    return String(this.data?.icon?.name || this.data?.iconName || '');
  }

  get showDelta(): boolean {
    return !!this.deltaText && this.data?.delta?.enabled !== false && this.data?.showDelta !== false;
  }

  get deltaClass(): string {
    return `delta-${this.deltaDirection}`;
  }

  loadStat(filters?: any): any {
    if (this.data?.staticValue !== undefined || this.data?.displayValue !== undefined) {
      return of({ [this.data.key || 'value']: this.data.staticValue, [this.deltaKey]: this.data?.delta?.value });
    }

    if (this.data?.directPost === true) {
      return this.http.post(this.data?.apiUrl, this.buildStandardPayload(filters));
    }

    if (this.data?.useStandardPayload === true || !!this.data?.functionName) {
      return this.standardConfigService.getConfig(
        this.data?.apiUrl,
        this.data,
        {
          ...this.normalizeRequestParamFilters(filters),
          useQueryParams: this.data?.useQueryParams || false,
        },
        this.data?.method || 'post',
      );
    }

    return this.configService.getConfig(
      this.data?.apiUrl,
      {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false,
      },
      this.data?.method || 'post',
    );
  }

  private get deltaKey(): string {
    return String(this.data?.delta?.key || this.data?.deltaKey || 'delta');
  }

  private buildStandardPayload(filters?: any): any {
    const requestParams = this.cleanRequestParams(filters?.requestParams || {});
    return {
      requestData: {
        ...(this.data?.defaultRequestData || this.data?.defaultPayload || {}),
        ...requestParams,
        ...(this.data?.customPayload || {}),
      },
      responseList: [],
      entity: this.data?.entity || 'default_entity',
      workflowId: this.data?.workflowId || 'default_workflow',
      endpoints: [
        {
          functionName: this.data?.functionName || 'DefaultService',
          version: this.data?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: this.data?.version || '1.0',
    };
  }

  private cleanRequestParams(params: any): any {
    const cleaned: any = {};
    Object.entries(params || {}).forEach(([key, value]) => {
      if (!key || key === 'loading' || key.startsWith('widget-')) {
        return;
      }
      cleaned[key] = this.normalizeRequestParamValue(value);
    });
    return cleaned;
  }

  private normalizeRequestParamFilters(filters?: any): any {
    if (!filters?.requestParams) {
      return filters;
    }

    return {
      ...filters,
      requestParams: this.cleanRequestParams(filters.requestParams),
    };
  }

  private normalizeRequestParamValue(value: any): any[] {
    const values = Array.isArray(value) ? value.flat(Infinity) : [value];
    return values.filter((item) => item !== null && item !== undefined && item !== '');
  }

  private getInitialFiltersForWidget(): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(this.data?.widgetId);
    if (navigationFilters) {
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters) {
      return undefined;
    }

    return this.chartFilterHandler.shouldReload(filters, this.data?.widgetId) ? filters : undefined;
  }

  private updateStat(res: any): void {
    const normalized = res?.responseList?.[0]?.data?.[0] ?? res?.data?.[0] ?? res;
    const parent = this.data?.parentKey
      ? normalized?.[this.data?.parentKey] ?? res?.[this.data?.parentKey]
      : normalized;
    const source = parent ?? normalized;
    const rawValue = this.data?.displayValue !== undefined ? this.data.displayValue : source?.[this.data.key];

    this.stat = this.formatValue(rawValue);

    this.deltaText = this.resolveDeltaText(source);
  }

  private formatValue(value: any): string {
    const formatted = this.data?.useFormatStat
      ? formatStat(value, this.data?.key || '', this.data?.prefix || '')
      : String(value ?? '');
    const suffix = this.data?.suffix;

    if (suffix === undefined || suffix === null || suffix === '' || formatted.endsWith(String(suffix))) {
      return formatted;
    }

    return `${formatted}${suffix}`;
  }

  private resolveDeltaText(source: any): string {
    const deltaConfig = this.data?.delta || {};
    const rawDelta = deltaConfig.value ?? source?.[this.deltaKey];
    if (rawDelta === undefined || rawDelta === null || rawDelta === '') {
      this.deltaDirection = 'neutral';
      return '';
    }

    const numericDelta = Number(String(rawDelta).replace('%', ''));
    this.deltaDirection = Number.isFinite(numericDelta)
      ? numericDelta > 0
        ? 'positive'
        : numericDelta < 0
          ? 'negative'
          : 'neutral'
      : 'neutral';

    if (typeof rawDelta === 'string' && rawDelta.includes('%')) {
      return rawDelta;
    }

    const decimals = Number.isFinite(Number(deltaConfig.decimals)) ? Number(deltaConfig.decimals) : 0;
    const suffix = deltaConfig.suffix ?? '%';
    const sign = deltaConfig.showSign === false || !Number.isFinite(numericDelta) || numericDelta < 0 ? '' : '+';
    const valueText = Number.isFinite(numericDelta) ? numericDelta.toFixed(decimals) : String(rawDelta);
    return `${sign}${valueText}${suffix}`;
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
